import type { CrosswordDoc } from './doc';
import type {
  CrosswordDraftPayload,
  CrosswordSource,
  ImportedCrosswordDraft,
} from '../../../../../services/admin';

// Adapters entre o modelo AUTORAL (doc) e os DTOs do backend. Toda a esquisitice do
// contrato mora aqui — o resto do módulo só conhece o `CrosswordDoc`.
//
//  - saída (create/replace/preview): doc → CrosswordDraftPayload (refs temporários).
//  - entrada (load): CrosswordSource (ids reais) → doc, derivando `ref = w{id}`.

/** ref estável a partir do id real do servidor (casa no /^[A-Za-z0-9_-]{1,32}$/). */
function refOf(id: number): string {
  return `w${id}`;
}

export function docToDraftPayload(doc: CrosswordDoc): CrosswordDraftPayload {
  const payload: CrosswordDraftPayload = {
    largura: doc.largura,
    altura: doc.altura,
    // O DTO exige tipo (passatempo com tipo nulo some dos filtros — R7). O modal de
    // criação sempre coleta um; o `?? 1` só cobre um preview de rascunho incompleto.
    tiposDePassatemposId: doc.tiposDePassatemposId ?? 1,
    palavras: doc.words.map((w) => ({
      ref: w.ref,
      texto: w.texto,
      x: w.x,
      y: w.y,
      direcao: w.direcao,
      chave: w.chave,
    })),
    casasMortas: doc.deadCells.map((c) => ({
      x: c.x,
      y: c.y,
      visivel: c.visivel,
      // slot nulo vira propriedade ausente (o DTO trata como opcional).
      ...(c.palavra1Ref != null ? { palavra1Ref: c.palavra1Ref } : {}),
      ...(c.palavra2Ref != null ? { palavra2Ref: c.palavra2Ref } : {}),
    })),
    casasDestaques: doc.highlights.map((h) => ({ x: h.x, y: h.y })),
  };
  if (doc.titulo.trim() !== '') payload.titulo = doc.titulo;
  return payload;
}

export function sourceToDoc(source: CrosswordSource): { doc: CrosswordDoc; editavel: boolean } {
  const doc: CrosswordDoc = {
    titulo: source.titulo ?? '',
    largura: source.largura,
    altura: source.altura,
    tiposDePassatemposId: source.tiposDePassatemposId,
    words: source.palavras.map((p) => ({
      ref: refOf(p.id),
      texto: p.texto,
      x: p.x,
      y: p.y,
      direcao: p.direcao,
      chave: p.chave,
    })),
    deadCells: source.casasMortas.map((c) => ({
      x: c.x,
      y: c.y,
      visivel: c.visivel,
      palavra1Ref: c.palavras1Id != null ? refOf(c.palavras1Id) : null,
      palavra2Ref: c.palavras2Id != null ? refOf(c.palavras2Id) : null,
    })),
    highlights: source.casasDestaques.map((d) => ({ x: d.x, y: d.y })),
  };
  return { doc, editavel: source.editavel };
}

export { refOf };

/** Rascunho importado (spec §5.1) → doc novo. É o inverso 1:1 do `docToDraftPayload`. */
export function draftToDoc(
  draft: ImportedCrosswordDraft,
  titulo: string,
  tiposDePassatemposId: number,
): CrosswordDoc {
  return {
    titulo,
    largura: draft.largura,
    altura: draft.altura,
    tiposDePassatemposId,
    words: draft.palavras.map((p) => ({
      ref: p.ref,
      texto: p.texto,
      x: p.x,
      y: p.y,
      direcao: p.direcao,
      chave: p.chave,
    })),
    deadCells: draft.casasMortas.map((c) => ({
      x: c.x,
      y: c.y,
      visivel: c.visivel,
      palavra1Ref: c.palavra1Ref,
      palavra2Ref: c.palavra2Ref,
    })),
    highlights: draft.casasDestaques.map((h) => ({ x: h.x, y: h.y })),
  };
}
